import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useModal } from '@/hooks/use-modal';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import {
  getBrowserTimezone,
  parseWallClockToLocalDate,
} from '@/utils/date-formatters/date-formatters';

import { computeElapsedSeconds } from '../helpers/compute-elapsed.helper';
import { useTimeTrackingStore } from '../store/time-tracking.store';
import { TimerStopMode } from '../types/dto/active-timer.dto';
import { CreateWorklogRequest } from '../types/dto/worklogs-summary.dto';
import { TimeEntryModalData } from '../types/time-tracking';
import { useActiveTimer } from './use-active-timer';
import { useActiveTimerProject } from './use-active-timer-project';
import { useDiscardTimer } from './use-discard-timer';
import { usePauseTimer } from './use-pause-timer';
import { useResumeTimer } from './use-resume-timer';
import { useStopTimer } from './use-stop-timer';
import { useUpdateTimer } from './use-update-timer';

/**
 * Encapsulates all timer control actions shared between TimeTracker and ProjectCard:
 * pause/resume, stop (with save modal), and discard (with confirmation modal).
 *
 * Handles success toasts and error toasts for every action.
 */
export const useTimerControls = () => {
  const { t } = useTranslation('authenticated.timeTracking');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { data: activeTimerData } = useActiveTimer();
  const activeTimer = activeTimerData?.timer;
  const activeTimerFetchedAt = activeTimerData?.fetchedAt;
  const fetchedTimerProject = useActiveTimerProject(activeTimer?.projectId);
  const setActiveTimerProject = useTimeTrackingStore(state => state.setActiveTimerProject);

  const { mutateAsync: pauseTimerMutation, isPending: isPausePending } = usePauseTimer();
  const { mutateAsync: resumeTimerMutation, isPending: isResumePending } = useResumeTimer();
  const { mutateAsync: stopTimerMutation, isPending: isStopPending } = useStopTimer();
  const { mutateAsync: updateTimerMutation, isPending: isUpdatePending } = useUpdateTimer();
  const { mutateAsync: discardTimerMutation, isPending: isDiscardPending } = useDiscardTimer();

  const { open: isStopModalOpen, onOpen: openStopModal, onClose: closeStopModal } = useModal();

  const {
    open: isDiscardModalOpen,
    onOpen: openDiscardModal,
    onClose: closeDiscardModal,
  } = useModal();

  const isPaused = activeTimer?.isPaused ?? false;
  const isLimitReached = activeTimer?.isLimitReached ?? false;

  const handleTogglePause = async () => {
    try {
      const timezone = getBrowserTimezone();
      if (isPaused) {
        await resumeTimerMutation({ timezone });
      } else {
        await pauseTimerMutation({ timezone });
      }
    } catch (error) {
      const key = isPaused
        ? 'api.error.activeTimerResumeFailed'
        : 'api.error.activeTimerPauseFailed';
      displayToastErrorFromApiWithFallback(error, `authenticated.timeTracking:${key}`);
    }
  };

  const handleStopSubmit = async (values: CreateWorklogRequest) => {
    try {
      await stopTimerMutation({
        projectId: values.projectId,
        timezone: getBrowserTimezone(),
        mode: TimerStopMode.Manual,
        // Timer endpoints expect a true UTC instant for `date` (paired with
        // `timezone`). `values.startDate` is a wall-clock `...Z` value (OPS-1485
        // worklog contract), so reinterpret its digits as local time and convert
        // back to real UTC — matching the original pre-OPS-1485 timer behaviour.
        date: parseWallClockToLocalDate(values.startDate).toISOString(),
        description: values.description ?? null,
        trackedMinutesOverride: values.trackedMinutes,
      });
      displayToastSuccess(t('api.error.activeTimerStopSuccess'));
      setActiveTimerProject(null);
      closeStopModal();
    } catch (error) {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:api.error.activeTimerStopFailed'
      );
    }
  };

  const handleSaveEntry = async (
    values: CreateWorklogRequest,
    project: DropdownSearchItem | null
  ) => {
    try {
      await updateTimerMutation({
        projectId: project ? Number(project.id) : null,
        description: values.description ?? null,
        // Unlike stop, the update endpoint expects local wall-clock time (which it
        // converts using `timezone`). `values.startDate` already carries the literal
        // wall-clock as a `...Z` value (OPS-1485 worklog contract), so send it as-is.
        startedAt: values.startDate,
        timezone: getBrowserTimezone(),
      });
      // Keep the persisted timer project in sync when it's changed mid-run.
      setActiveTimerProject(project);
      closeStopModal();
    } catch (error) {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:api.error.activeTimerUpdateFailed'
      );
    }
  };

  const handleDiscard = async () => {
    try {
      await discardTimerMutation();
      displayToastSuccess(t('api.error.activeTimerDiscardSuccess'));
      setActiveTimerProject(null);
      closeDiscardModal();
    } catch (error) {
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:api.error.activeTimerDiscardFailed'
      );
    }
  };

  const timeTrack = useMemo<TimeEntryModalData | undefined>(() => {
    if (!activeTimer || activeTimerFetchedAt === undefined) return undefined;

    // startedAt is wall-clock time with a misleading Z suffix — read UTC components
    // directly so no local timezone offset is applied (mirrors normalizeToWallTimeUTC).
    const d = new Date(activeTimer.startedAt);
    const startTime = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    const totalElapsedSeconds = computeElapsedSeconds(activeTimer, activeTimerFetchedAt);

    return {
      trackedHours: totalElapsedSeconds / 3600,
      trackedMinutes: Math.round(totalElapsedSeconds / 60),
      date: activeTimer.startedAt,
      startTime,
      endTime: undefined,
      description: activeTimer.description || '',
      project: fetchedTimerProject
        ? {
            projectId: Number(fetchedTimerProject.id),
            projectName: fetchedTimerProject.name,
            logoUrl: fetchedTimerProject.avatarLink || '',
            colorHex: fetchedTimerProject.colorHex || '',
          }
        : undefined,
    };
  }, [activeTimer, activeTimerFetchedAt, fetchedTimerProject]);

  // Any in-flight timer action — used to disable controls and avoid double-clicks
  // across pause/resume, stop, update and discard while the previous one is pending.
  const isAnyActionPending =
    isPausePending || isResumePending || isStopPending || isUpdatePending || isDiscardPending;

  return {
    activeTimer,
    activeTimerFetchedAt,
    fetchedTimerProject,
    isPaused,
    isLimitReached,
    isStopModalOpen,
    openStopModal,
    closeStopModal,
    timeTrack,
    isDiscardModalOpen,
    openDiscardModal,
    closeDiscardModal,
    handleTogglePause,
    handleStopSubmit,
    handleSaveEntry,
    handleDiscard,
    isPausePending,
    isResumePending,
    isTogglePausePending: isPausePending || isResumePending,
    isStopPending,
    isUpdatePending,
    isDiscardPending,
    isAnyActionPending,
  };
};
